import { Injectable } from '@angular/core';
import { ResourceEffect } from '@app/core/store/effects/resource.effect';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, from, withLatestFrom } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import {
  LoadWorkOrdersSuccess,
  LoadWorkOrder,
  WorkOrderActionTypes,
  LoadWorkOrderObjectsSuccess,
  UpdateWorkLineProgress,
  UpdateWorkLineQuantity,
  UpdateWorkLineCableLength,
  MarkWorkLineAsComplete,
  ReOpenWorkLine,
} from '@dwo/actions';
import { State } from '@dwo/reducers';
import { WorkOrderService } from '@app/core/services/work-order.service';
import { getCurrentWorkOrder, getHierarchy } from '@app/core/store/selectors/work-order.selector';
import { ChangeLogService } from '@app/core/services/change-log.service';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { WorkLineDto, WorkOrderObjectHierarchyDto } from '@app/generated/model';
import {
  MARK_WORK_LINE_AS_COMPLETE,
  MARK_WORK_LINE_AS_OPEN,
  UPDATE_CABLE_LENGTH_ON_WORK_LINE,
  UPDATE_INSTALLED_QUANTITY_ON_WORK_LINE,
} from '@app/actions';

@Injectable()
export class WorkOrderEffect extends ResourceEffect {
  constructor(
    readonly actions$: Actions,
    readonly workOrderService: WorkOrderService,
    readonly store: Store<State>,
    private changeLogService: ChangeLogService,
    private offlineData: OfflineDataDb,
  ) {
    super(actions$, workOrderService, store);
  }

  loadWorkOrder$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadWorkOrder>(WorkOrderActionTypes.LoadWorkOrder),
        this.fetchResource('loadWorkOrder', LoadWorkOrdersSuccess, (action: LoadWorkOrder) => [action.workOrderId]),
      ),
  );

  loadWorkOrderOperations$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType<LoadWorkOrder>(WorkOrderActionTypes.LoadWorkOrderObjects),
        this.fetchResource('loadWorkOrderOperations', LoadWorkOrderObjectsSuccess, (action: LoadWorkOrder) => [
          action.workOrderId,
        ]),
      ),
  );

  updateWorkLineProgress$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateWorkLineProgress>(WorkOrderActionTypes.UpdateWorkLineProgress),
        withLatestFrom(this.store.select(getCurrentWorkOrder), this.store.select(getHierarchy)),
        concatMap(([action, workOrder, hierarchy]) =>
          from(
            (() => {
              const workLineInstalledQuantities = this.resolveWorkLineInstalledQuantities(
                hierarchy,
                action.workLineIds,
              );
              const payload: Record<string, unknown> = {
                workOrderId: workOrder?.id,
                workOrderObjectIds: [],
                workLineIds: action.workLineIds,
                progress: action.progress,
                resetStatus: false,
                workLineInstalledQuantities,
              };

              if (workLineInstalledQuantities.length === 1) {
                payload.installedQuantity = workLineInstalledQuantities[0].installedQuantity;
              }

              return this.queueWorkLineChange('WO_UPDATE_OBJ_PROGRESS', payload, workOrder?.id, hierarchy);
            })(),
          ),
        ),
      ),
    { dispatch: false },
  );

  updateWorkLineQuantity$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateWorkLineQuantity>(WorkOrderActionTypes.UpdateWorkLineQuantity),
        withLatestFrom(this.store.select(getCurrentWorkOrder), this.store.select(getHierarchy)),
        concatMap(([action, workOrder, hierarchy]) =>
          from(
            this.queueWorkLineChange(
              UPDATE_INSTALLED_QUANTITY_ON_WORK_LINE,
              {
                workOrderId: workOrder?.id,
                workLineId: action.workLineId,
                referenceId: action.workLineId,
                installedQuantity: action.quantity,
              },
              workOrder?.id,
              hierarchy,
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  updateWorkLineCableLength$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateWorkLineCableLength>(WorkOrderActionTypes.UpdateWorkLineCableLength),
        withLatestFrom(this.store.select(getCurrentWorkOrder), this.store.select(getHierarchy)),
        concatMap(([action, workOrder, hierarchy]) =>
          from(
            this.queueWorkLineChange(
              UPDATE_CABLE_LENGTH_ON_WORK_LINE,
              {
                workOrderId: workOrder?.id,
                workLineId: action.workLineId,
                referenceId: action.workLineId,
                installedQuantity: action.length,
                externalLocationId: action.externalLocationId,
              },
              workOrder?.id,
              hierarchy,
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  markWorkLineAsComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<MarkWorkLineAsComplete>(WorkOrderActionTypes.MarkWorkLineAsComplete),
        withLatestFrom(this.store.select(getCurrentWorkOrder), this.store.select(getHierarchy)),
        concatMap(([action, workOrder, hierarchy]) =>
          from(
            this.queueWorkLineChange(
              MARK_WORK_LINE_AS_COMPLETE,
              {
                workOrderId: workOrder?.id,
                workLineId: action.workLineId,
                referenceId: action.workLineId,
              },
              workOrder?.id,
              hierarchy,
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  reOpenWorkLine$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ReOpenWorkLine>(WorkOrderActionTypes.ReOpenWorkLine),
        withLatestFrom(this.store.select(getCurrentWorkOrder), this.store.select(getHierarchy)),
        concatMap(([action, workOrder, hierarchy]) =>
          from(
            this.queueWorkLineChange(
              MARK_WORK_LINE_AS_OPEN,
              {
                workOrderId: workOrder?.id,
                workLineId: action.workLineId,
                referenceId: action.workLineId,
              },
              workOrder?.id,
              hierarchy,
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  private async queueWorkLineChange(
    eventType: string,
    payload: Record<string, unknown>,
    workOrderId: string | undefined,
    hierarchy: unknown,
  ): Promise<void> {
    if (!workOrderId) {
      return;
    }

    await this.changeLogService.publish(eventType, payload, [workOrderId]);

    await this.offlineData.workOrderDataItems.where({ workOrderId, type: 'OBJECTS' }).modify((item) => {
      item.data = hierarchy;
    });
  }

  private resolveWorkLineInstalledQuantities(
    hierarchy: WorkOrderObjectHierarchyDto[] | null | undefined,
    workLineIds: String[],
  ): Array<{ workLineId: string; installedQuantity: number | null }> {
    const ids = new Set(workLineIds.map((workLineId) => workLineId?.toString()).filter((workLineId) => !!workLineId));
    if (!ids.size || !hierarchy?.length) {
      return [];
    }

    const allWorkLines = this.flattenWorkLines(hierarchy);
    return allWorkLines
      .filter((workLine) => !!workLine.id && ids.has(workLine.id))
      .map((workLine) => ({
        workLineId: workLine.id!,
        installedQuantity: workLine.installedQuantity ?? null,
      }));
  }

  private flattenWorkLines(hierarchy: WorkOrderObjectHierarchyDto[]): WorkLineDto[] {
    const result: WorkLineDto[] = [];

    const traverse = (nodes: WorkOrderObjectHierarchyDto[]) => {
      nodes.forEach((node) => {
        if (node.workLines?.length) {
          result.push(...node.workLines);
        }

        if (node.children?.length) {
          traverse(node.children as WorkOrderObjectHierarchyDto[]);
        }
      });
    };

    traverse(hierarchy);
    return result;
  }
}
